import { ITourSettings, TourVisibility } from '../../models';
import { LocationDescriptorObject, LocationState, Path } from 'history';
import { ImageDetailPath, ImageListPath } from './utils/imagePathHelper';
import { IImageState, ISingleChoiceTaskState, ITourState, ITourStopState } from './models/state';
import { IAsyncError, ImageUploadResponse } from '../../common';
import { ICreateTourModel } from './models/Tour';
import { createAction } from '../../utils/reduxUtils';


const updateTourField = <F extends keyof ITourState>(field: F, value: ITourState[F], save = false) => ({
  type: '@createTour/UPDATE_TOUR_FIELD' as const,
  payload: { field, value },
  meta: { save },
});
updateTourField.type = '@createTour/UPDATE_TOUR_FIELD';

const sanitizeTourField = <F extends keyof ITourState>(field: F, value: ITourState[F], save = false) => ({
  type: '@createTour/SANITIZE_TOUR_FIELD' as const,
  payload: { field, value },
  meta: { save },
});
sanitizeTourField.type = '@createTour/SANITIZE_TOUR_FIELD';


const updateTourStopField = <F extends keyof ITourStopState>(localId: number, field: F, value: ITourStopState[F], save = false) => ({
  type: '@createTour/UPDATE_TOUR_STOP_FIELD' as const,
  payload: { localId, field, value },
  meta: { save },
});
updateTourStopField.type = '@createTour/UPDATE_TOUR_STOP_FIELD';

const sanitizeTourStopField = <F extends keyof ITourStopState>(localId: number, field: F, value: ITourStopState[F], save = false) => ({
  type: '@createTour/SANITIZE_TOUR_STOP_FIELD' as const,
  payload: { localId, field, value },
  meta: { save },
});
sanitizeTourStopField.type = '@createTour/SANITIZE_TOUR_STOP_FIELD';

const updateTask = <F extends keyof ISingleChoiceTaskState>(localId: number, field: F, value: ISingleChoiceTaskState[F]) => ({
  type: '@createTour/CREATE_OR_UPDATE_TASK' as const,
  payload: { localId, field, value },
});
updateTask.type = '@createTour/CREATE_OR_UPDATE_TASK';

const sanitizeTask = <F extends keyof ISingleChoiceTaskState>(localId: number, field: F, value: ISingleChoiceTaskState[F]) => ({
  type: '@createTour/SANITIZE_TASK' as const,
  payload: { localId, field, value },
});
sanitizeTask.type = '@createTour/SANITIZE_TASK';

const updateTourVisibility = {
  request: createAction('@createTour/UPDATE_TOUR_VISIBILITY_REQUEST')<TourVisibility>,
  confirmed: createAction('@createTour/UPDATE_TOUR_VISIBILITY_CONFIRMED')<TourVisibility>,
};

const startEditingTour = createAction('@createTour/START_EDITING_TOUR')<void>;

const rollbackTourState = createAction('@createTour/ROLLBACK_TOUR_STATE')<void>;

const createTourStop = createAction('@createTour/CREATE_TOURSTOP')<void>;

const resetActive = createAction('@createTour/RESET_ACTIVE')<void>;

const deleteTourStop = (localId: number) => ({
  type: '@createTour/DELETE_TOUR_STOP' as const,
  payload: localId,
  meta: { save: true },
});
deleteTourStop.type = '@createTour/DELETE_TOUR_STOP';

const populateTourState = {
  request: createAction('@createTour/POPULATE_TOUR_STATE_REQUEST')<string | undefined>,
  success: createAction('@createTour/POPULATE_TOUR_STATE_SUCCESS')<ICreateTourModel>,
  failure: createAction('@createTour/POPULATE_TOUR_STATE_FAILURE')<IAsyncError>,
};
const insertFakeTour = createAction('@app/INSERT_FAKE_TOUR')<void>;

const toggleIsRoundTrip = createAction('@createTour/TOGGLE_IS_ROUND_TRIP')<void>;

const toggleSettingsField = <S extends keyof ITourSettings>(settingName: S, value: any) => ({
  type: '@createTour/TOGGLE_SETTINGS_FIELD' as const,
  payload: { settingName, value },
});
toggleSettingsField.type = '@createTour/TOGGLE_SETTINGS_FIELD';


const saveTourRequest = (updateSnapshot = false) => ({
  type: '@createTour/SAVE_TOUR_REQUEST' as const,
  payload: undefined,
  meta: { updateSnapshot },
});
saveTourRequest.type = '@createTour/SAVE_TOUR_REQUEST';

const saveTourAsync = {
  request: saveTourRequest,
  success: createAction('@createTour/SAVE_TOUR_SUCCESS')<ICreateTourModel>,
  failure: createAction('@createTour/SAVE_TOUR_FAILURE')<IAsyncError>,
};


const uploadImageAsync = {
  request: createAction('@createTour/UPLOAD_IMAGE_REQUEST')<{
    file: Blob;
    parentUrl: LocationDescriptorObject<LocationState> | Path;
    detailPath: ImageDetailPath;
  }>,
  success: createAction('@createTour/UPLOAD_IMAGE_SUCCESS')<{
    imageInfo: ImageUploadResponse;
    detailPath: ImageDetailPath;
  }>,
  failure: createAction('@createTour/UPLOAD_IMAGE_FAILURE')<IAsyncError>,
};

const createImage = createAction('@createTour/CREATE_IMAGE')<ImageDetailPath>;

const updateImage = createAction('@createTour/UPDATE_IMAGE')<{
  path: ImageListPath;
  localId: number;
  value: Partial<IImageState>;
}>;

const guidanceDialog = {
  show: createAction('@createTour/SHOW_GUIDANCE_DIALOG')<void>,
  hide: createAction('@createTour/HIDE_GUIDANCE_DIALOG')<void>,
};

const deleteImage = (listPath: ImageListPath, localId: number) => ({
  type: '@createTour/DELETE_IMAGE' as const,
  payload: { listPath, localId },
  meta: { save: true },
});
deleteImage.type = '@createTour/DELETE_IMAGE';

const reorderImages = (listPath: ImageListPath, newIndex: number, oldIndex: number) => ({
  type: '@createTour/REORDER_IMAGES' as const,
  payload: { listPath, newIndex, oldIndex },
  meta: { save: true },
});
reorderImages.type = '@createTour/REORDER_IMAGES';



const reorderTourStops = (newIndex: number, oldIndex: number) => ({
  type: '@createTour/REORDER_TOUR_STOPS' as const,
  payload: { newIndex, oldIndex },
  meta: { save: true },
});
reorderTourStops.type = '@createTour/REORDER_TOUR_STOPS';



const updatePreviewTourStop = (stopId: number) => ({
  type: '@createTour/UPDATE_PREVIEW_TOUR_STOP' as const,
  payload: stopId,
  meta: { save: true },
});
updatePreviewTourStop.type = '@createTour/UPDATE_PREVIEW_TOUR_STOP';

const share = {
  link: createAction('@createTour/SHARE_LINK')<void>,
  code: createAction('@createTour/SHARE_CODE')<void>,
};

const requestReview = {
  request: createAction('@createTour/REQUEST_REVIEW_REQUEST')<void>,
  success: createAction('@createTour/REQUEST_REVIEW_SUCCESS')<void>,
  failure: createAction('@createTour/REQUEST_REVIEW_FAILURE')<IAsyncError>,
};

const cloneTour = {
  request: createAction('@createTour/CLONE_TOUR_REQUEST')<void>,
  start: createAction('@createTour/CLONE_TOUR_START')<void>,
  success: createAction('@createTour/CLONE_TOUR_SUCCESS')<ICreateTourModel>,
  failure: createAction('@createTour/CLONE_TOUR_FAILURE')<IAsyncError>,
  cancel: createAction('@createTour/CLONE_TOUR_CANCEL')<void>,
};

const translateTour = {
  request: createAction('@createTour/TRANSLATE_TOUR_REQUEST')<string>,
  success: createAction('@createTour/TRANSLATE_TOUR_SUCCESS')<ICreateTourModel>,
  failure: createAction('@createTour/TRANSLATE_TOUR_FAILURE')<IAsyncError>,
  cancel: createAction('@createTour/TRANSLATE_TOUR_CANCEL')<void>,
};

const actions = {
  saveTourAsync,
  updateTourField,
  sanitizeTourField,
  uploadImageAsync,
  startEditingTour,
  rollbackTourState,
  createTourStop,
  updateTourStopField,
  sanitizeTourStopField,
  deleteTourStop,
  resetActive,
  updateTask,
  sanitizeTask,
  populateTourState,
  toggleSettingsField,
  createImage,
  updateImage,
  reorderImages,
  deleteImage,
  reorderTourStops,
  insertFakeTour,
  updateTourVisibility,
  updatePreviewTourStop,
  share,
  requestReview,
  toggleIsRoundTrip,
  guidanceDialog,
  cloneTour,
  translateTour,
};

export default actions;